import { defineComponent as _defineComponent } from 'vue'
import { twitter } from "vue-twitter";
import { DataTableHeader } from "vuetify";

import AdditionalRoleOffice from "@/components/profile/AdditionalRoleOffice.vue";
import AdditionalRolesRow from "@/components/profile/AdditionalRolesRow.vue";
import BriefcaseDialog from "@/components/BriefcaseFolderMoveDialog.vue";
import Dialog from "@/components/vuetify-wrappers/Dialog.vue";
import ExportButton from "@/components/buttons/ExportButton.vue";
import NjBtn from "@/components/vuetify-wrappers/NjButton.vue";
import OfficeAddress from "@/components/profile/OfficeAddress.vue";
import ProfileCard from "@/components/ProfileCard.vue";
import ProfileItem from "@/components/profile/ProfileItem.vue";
import ProfilePanel from "@/components/profile/ProfilePanel.vue";
import ProfileSubModuleCards from "@/components/profile/ProfileSubModuleCards.vue";
import ProfileWrapper from "@/components/profile/ProfileWrapper.vue";
import { Route } from "vue-router";
import { ApiService, LogService, SnackbarService } from "@/services";
import {
  AdditionalRoleResponse,
  MetadataKeys,
  Profile as ProfileObj,
  VignetteHelpers,
  VignetteRoutes,
} from "@/types";
import { ParsedResponse } from "@/types/utils/CaseTypes";
import { useSessionStore } from "@/pinia/session";
import { useAppStore } from "@/pinia/app";
import { computed, onBeforeMount, ref } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { useViewAnalytics } from "@/composition/useViewAnalytics";
import DownloadWithCustomLogoBtn from "@/components/buttons/DownloadWithCustomLogoBtn.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'Profile',
  setup(__props) {

const route = useRoute();
const router = useRouter();

// <editor-fold desc="App Store Properties">
const appStore = useAppStore();
// </editor-fold>

// <editor-fold desc="Session Store Properties">
const sessionStore = useSessionStore();
const hasCustomLogo = computed(() => sessionStore.hasCustomLogo);
const hasVignetteAccess = computed(
  () => sessionStore.hasVignetteAccess ?? false
);
// </editor-fold>

// <editor-fold desc="Profile Properties">
const profile = ref<ProfileObj | null>(null);
const isLoaded = ref(false);
const isProfileVisible = computed(
  () => profile.value?.isUnlocked || hasAccessToProfile.value
);
const modules = computed<number[]>(
  () => profile.value?.subscriptionModules || []
);
const socialLinks = computed(() => {
  const facebook = {
    title: "Facebook",
    link: facebookLink.value,
  };
  const twitter = {
    title: "Twitter",
    link: twitterLink.value,
  };
  const youtube = {
    title: "YouTube",
    link: youtubeLink.value,
  };
  const instagram = {
    title: "Instagram",
    link: instagramLink.value,
    display: instagramHandle.value,
  };
  const linkedIn = {
    title: "LinkedIn",
    link: linkedInLink.value,
  };
  const website = {
    title: "Website",
    link: websiteLink.value,
  };
  return [
    ...(facebookLink.value ? [facebook] : []),
    ...(twitterLink.value ? [twitter] : []),
    ...(youtubeLink.value ? [youtube] : []),
    ...(instagramLink.value ? [instagram] : []),
    ...(linkedInLink.value ? [linkedIn] : []),
    ...(websiteLink.value ? [website] : []),
  ];
});
const facebookLink = computed(() => profile.value?.facebookUrl ?? "");
const getTwitterLinkFromString = (twitterString: string): string => {
  if (twitterString) {
    if (twitterString.includes("http")) {
      return twitterString;
    } else if (twitterString.includes("@")) {
      return `https://twitter.com/${twitterString.replace("@", "")}`;
    } else {
      return `https://twitter.com/${twitterString}`;
    }
  }
  return "";
};
const twitterLink = computed(() => {
  if (profile.value?.twitterUsernameKnowwho) {
    return getTwitterLinkFromString(profile.value.twitterUsernameKnowwho);
  } else if (profile.value?.twitterUsername) {
    return getTwitterLinkFromString(profile.value.twitterUsername);
  }
  return "";
});
const instagramLink = computed(() =>
  profile.value?.instagramUsername
    ? `https://www.instagram.com/${profile.value.instagramUsername}`
    : ""
);
const instagramHandle = computed(() =>
  profile.value?.instagramUsername ? `@${profile.value.instagramUsername}` : ""
);
const youtubeLink = computed(() => profile.value?.youtube ?? "");
const linkedInLink = computed(() => profile.value?.linkedInUrl ?? "");
const websiteLink = computed(() => profile.value?.websiteUrl ?? "");
const presentationCenterLink = computed(() =>
  profile.value?.presentationCenterUrl
    ? appStore.djangoSiteOrigin + profile.value.presentationCenterUrl
    : ""
);
const emailLink = computed(() =>
  profile.value?.email.includes("@")
    ? `mailto:${profile.value.email}`
    : profile.value?.email ?? ""
);
const docxUrl = computed(
  () =>
    (profile.value?.isUnlocked || hasAccessToProfile.value) &&
    (profile.value?.docxUrl ?? "")
);
const docxUrlWithCustomLogo = computed(
  () =>
    (profile.value?.isUnlocked || hasAccessToProfile.value) &&
    (`${profile.value?.docxUrl}?custom_logo=True` ?? "")
);
const pdfUrl = computed(
  () =>
    (profile.value?.isUnlocked || hasAccessToProfile.value) &&
    (profile.value?.pdfUrl ?? "")
);

/**
 * Computes whether to show the electoral history section.
 * If the electoral history is not empty, returns true.
 * Otherwise, returns true if the first elected date and number of terms are set.
 */
const showElectoralHistory = computed<boolean>(() => {
  const electoralHistory = profile.value?.electoralHistory ?? [];
  return electoralHistory.length > 0 || !!firstElectedWithTenureText.value;
});

/**
 * Computes the text to display for the first elected with tenure.
 * If the first elected date is not set, returns an empty string (even if the number of terms is set).
 * If the number of terms is not set, returns just the first elected date.
 * If the number of terms is 0, returns "${firstElectedDate}, First Term".
 * If the number of terms is 1, returns "${firstElectedDate}, 1 term".
 * Otherwise, returns "${firstElectedDate}, ${numberOfTerms} terms".
 */
const firstElectedWithTenureText = computed(() => {
  let firstElected = profile.value?.firstElected.trim() ?? "";
  const tenure = profile.value?.numberTenuresInSeat ?? 0;
  const getTenureString = (tenure: number): string => {
    if (!tenure) return "";
    if (tenure === 1) return "First Term";
    return tenure + " terms";
  };
  if (!!firstElected && !!tenure) firstElected += ",";
  return `${firstElected} ${getTenureString(tenure)}`.trim();
});
// </editor-fold>

// <editor-fold desc="UI Properties">
const display404 = ref(false);
const noAccessBannerVisible = ref(false);
const hasAccessToProfile = ref(false);
const docxBtnClicked = ref(false);
const pdfBtnClicked = ref(false);
const docxWithCustomLogoBtnClicked = ref(false);
const requestCustomLogoDialog = ref(false);
const panelsToBeOpened = ref<string[]>([
  "Quick Summary",
  "Congressional Information",
  "Legislative Information",
  "Congressional Staff",
  "Legislative Staff",
  "Gubernatorial Staff",
  "Web and Social Media",
  "Office Contact Information",
]);

// </editor-fold>

const pageName = ref("");
const { pageTitle, setAnalyticsMetadata, pushPageViewEvent } =
  useViewAnalytics(pageName);

const congressionalStaffersHeaders = ref<DataTableHeader[]>([
  {
    text: "Name",
    value: "stafferFullName",
    sortable: true,
    filterable: true,
    align: "start",
    sort: (a: string, b: string) => {
      return a.localeCompare(b);
    },
  },
  {
    text: "Job Categories",
    value: "rolenames",
    sortable: true,
    filterable: true,
    align: "start",
  },
]);

const focusAreaHeader = {
  text: "Focus Areas",
  value: "stafferInterests",
  sortable: true,
  filterable: true,
  align: "start",
};
const stafferSearch = ref("");

const formatCurrency = (value: number): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "(Unavailable)";
  }
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

const stateRoute = computed(() => {
  if (profile.value?.state?.name) {
    const id = profile.value.state.id;
    const name = VignetteHelpers.getProfileNameSlug(profile.value.state.name);
    return { name: VignetteRoutes.State, params: { id, name } };
  }
  return "";
});

const districtRoute = computed(() => {
  if (profile.value?.district?.name) {
    const id = profile.value.district.legDistrictId;
    const name = VignetteHelpers.getProfileNameSlug(
      profile.value.district.name
    );
    return { name: VignetteRoutes.District, params: { id, name } };
  }
  return "";
});

const hierarchyChips = computed<
  {
    id?: number;
    title: string;
    link: Pick<Route, "name" | "params"> | string;
    tooltip?: string;
  }[]
>(() => {
  const roleMapper = (role: ParsedResponse<AdditionalRoleResponse>) => {
    if (role.recordType === "agencyperson") {
      return {
        id: role.agency ? role.agency.id : role.id,
        title: role.positionSummary,
        link: role.agency
          ? `/agency/${
              role.agency.id +
              VignetteHelpers.getProfileNameSlug(role.agency.name)
            }`
          : "",
        tooltip: role.agency?.agencyFull ?? "",
      };
    }
    return { title: "", link: "" };
  };

  const result: {
    id?: number;
    title: string;
    link: Pick<Route, "name" | "params"> | string;
    tooltip?: string;
  }[] = [];
  // all chips for roles
  if (profile.value?.additionalRoles) {
    profile.value.additionalRoles.forEach((role) => {
      role.agency?.id && result.push(roleMapper(role));
    });
  }
  if (profile.value?.agency) {
    const agency = profile.value.agency;
    const agencyId = agency.id;
    const agencyIdAlreadyExistsInAdditionalRoles = result.some(
      (role) => role?.id === agencyId
    );
    // make sure we aren't adding a duplicate
    if (!agencyIdAlreadyExistsInAdditionalRoles) {
      result.push({
        title: agency.agencyFull || agency.name,
        link: `/agency/${agencyId}${VignetteHelpers.getProfileNameSlug(
          profile.value.agency.name
        )}`,
      });
    }
  }
  // chip for state
  if (profile.value?.state?.id) {
    result.push({
      title: profile.value.state.name,
      link: stateRoute.value,
    });
  }
  // make sure the chips aren't too long (35 chars)
  result.forEach((chip) => {
    if (chip.title?.length > 35) {
      let index = chip.title.substring(0, 35).lastIndexOf(" ");
      if (chip.title.charAt(index - 1) === ",") index--;
      chip.tooltip = chip.title;
      chip.title = chip.title.substring(0, index) + "...";
    }
  });
  // chip for district (skip shrinking chip size for these)
  if (profile.value?.district?.id) {
    result.push({
      title: profile.value.district.name,
      link: districtRoute.value,
    });
  }

  return result;
});

const clickPdfBtn = (): void => {
  pdfBtnClicked.value = true;
  setTimeout(() => {
    pdfBtnClicked.value = false;
  }, 4000);
};

const clickDocxBtn = (): void => {
  docxBtnClicked.value = true;
  setTimeout(() => {
    docxBtnClicked.value = false;
  }, 4000);
};

const requestCustomLogo = (): void => {
  ApiService.postUserContact("", true).then((response) => {
    if (response.statusCode === 200) {
      LogService.info(`Profile.requestCustomLogo is successful.`);
      SnackbarService.displaySnackbar(
        "Request for custom logo successfully submitted.",
        "primary"
      );
    } else {
      LogService.error(
        `Profile.requestCustomLogo failed: '${response.error_message}'`
      );
      SnackbarService.displaySnackbar("An error occurred, please try again.");
    }
  });

  requestCustomLogoDialog.value = false;
};

const getAllCmsTags = (): string => {
  let result = "";

  if (profile.value != null) {
    if (!!profile.value.primaryProfileType) {
      result += profile.value.primaryProfileType;
    }
    if (profile.value.primaryProfileTypesAdditional.length > 0) {
      if (result.length > 0) result += ",";
      result += profile.value.primaryProfileTypesAdditional.join(",");
    }
    if (profile.value.secondaryProfileTypes.length > 0) {
      if (result.length > 0) result += ",";
      result += profile.value.secondaryProfileTypes.join(",");
    }
    if (profile.value.subscriptionModules.length > 0) {
      if (result.length > 0) result += ",";
      result += profile.value.subscriptionModules
        .map((module) => appStore.getSubscriptionModuleName(module))
        .join(",");
    }
  }
  return result;
};

const contentString = computed<string>(() => {
  if (!profile.value?.id) return "";
  if (route.name === VignetteRoutes.ProfileKW) {
    return `knowwho-person-${profile.value.id}`;
  } else {
    return `profile-${profile.value.id}`;
  }
});
const setAnalyticsMetadataAndSendEvent = (): void => {
  setAnalyticsMetadata(MetadataKeys.SITESECTION, "AlmanacPlus");
  if (profile.value != null) {
    setAnalyticsMetadata(MetadataKeys.PAGE_TYPE, "Database");
    setAnalyticsMetadata(MetadataKeys.CONTENT_TITLE, pageName.value);
    setAnalyticsMetadata(MetadataKeys.CMS_CONTENT_ID, contentString.value);
    setAnalyticsMetadata(MetadataKeys.CMS_TAGS, getAllCmsTags());
    setAnalyticsMetadata(
      MetadataKeys.VIGNETTE_PRIMARY_PROFILE_TYPE,
      profile.value.primaryProfileType
    );
    pushPageViewEvent();
  }
};

onBeforeMount(async () => {
  const profileIdParam = route.params.id;
  const profileNameParam = route.params.name;
  LogService.debug(
    `Profile.vue: profileIdParam: ${profileIdParam}, profileNameParam: ${profileNameParam}`
  );

  const profileId = +profileIdParam;

  if (isNaN(profileId)) {
    LogService.warn(
      `Profile.vue: Invalid profileId is requested: '${profileIdParam}'`
    );
  } else {
    await ApiService.refreshBriefcase();
    try {
      profile.value = await ApiService.getProfile(
        profileId,
        route.name === VignetteRoutes.ProfileKW
      );
      if (profile.value != null) {
        profile.value.subscriptionModules.forEach((module) => {
          if (sessionStore.hasVignetteModuleAccess(module)) {
            hasAccessToProfile.value = true;
          }
        });
        pageName.value = profile.value.preferredNameDisplay
          ? profile.value.preferredNameDisplay
          : "";
        if (!hasAccessToProfile.value && !profile.value.isUnlocked) {
          LogService.warn(
            `Profile.vue: User doesn't have access to this profile.`
          );
          noAccessBannerVisible.value = true;
        }
      }
      const hasFocusAreas = profile.value?.staffers?.some(
        (staffer) => staffer.stafferInterests?.length > 0
      );
      if (hasFocusAreas) {
        congressionalStaffersHeaders.value.push(focusAreaHeader);
      }
      setAnalyticsMetadataAndSendEvent();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (err?.code === 303 && err?.meta?.id) {
        router.push({
          name: VignetteRoutes.Profile,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          params: { id: err.meta.id },
        });
        return;
      }
      LogService.warn(
        `Profile.vue: Error retrieving profileId: '${profileId}'`
      );
      display404.value = true;
    } finally {
      isLoaded.value = true;
    }
  }
});

return { __sfc: true,route, router, appStore, sessionStore, hasCustomLogo, hasVignetteAccess, profile, isLoaded, isProfileVisible, modules, socialLinks, facebookLink, getTwitterLinkFromString, twitterLink, instagramLink, instagramHandle, youtubeLink, linkedInLink, websiteLink, presentationCenterLink, emailLink, docxUrl, docxUrlWithCustomLogo, pdfUrl, showElectoralHistory, firstElectedWithTenureText, display404, noAccessBannerVisible, hasAccessToProfile, docxBtnClicked, pdfBtnClicked, docxWithCustomLogoBtnClicked, requestCustomLogoDialog, panelsToBeOpened, pageName, pageTitle, setAnalyticsMetadata, pushPageViewEvent, congressionalStaffersHeaders, focusAreaHeader, stafferSearch, formatCurrency, stateRoute, districtRoute, hierarchyChips, clickPdfBtn, clickDocxBtn, requestCustomLogo, getAllCmsTags, contentString, setAnalyticsMetadataAndSendEvent, twitter, AdditionalRoleOffice, AdditionalRolesRow, BriefcaseDialog, Dialog, ExportButton, NjBtn, OfficeAddress, ProfileCard, ProfileItem, ProfilePanel, ProfileSubModuleCards, ProfileWrapper, VignetteHelpers, VignetteRoutes, DownloadWithCustomLogoBtn }
}

})